import { Skeleton, Stack, Typography } from '@mui/material';
import { noop } from 'lodash';
import { ReactNode, useCallback, useState } from 'react';
import { FieldArrayWithId, useWatch } from 'react-hook-form';

import { Divider } from '@/components/Divider';
import { Button } from '@/components/form/baseInputs/Button';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Reflect01Icon } from '@/components/icons/Reflect01Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { Card } from '@/components/layout/Card/Card';
import { RichListItem } from '@/components/lists/RichListItem/RichListItem';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { ResiduaryContextualHelp } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/ResiduaryContextualHelp';
import { ToggleGrowAllByInflationMenuItem } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/ToggleGrowAllByInflationMenuItem';
import { PrimaryClientDeathSectionActions } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionActions';
import { PrimaryClientDeathSectionDistributionStatus } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSectionDistributionStatus';
import { PrimaryClientDeathEvent } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { COLORS } from '@/styles/tokens/colors';
import { DispositiveProvisionTemplateKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import {
  defaultRecipient,
  DISPOSITIVE_PROVISIONS_FORM_NAMESPACE,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsFormShape,
  DispositiveProvisionsModalDetails,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DispositiveProvisionsRecipients } from '../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipients';
import {
  SplitDispositionModal,
  SplitDispositionModalResult,
} from '../DispositiveProvisionsForm/SplitDispositionModal';
import {
  DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE,
  DispositiveProvisionsTemplateShape,
  ProRataValue,
} from './DispositiveProvisionsTemplateSplitScreenModal.types';
import {
  mapClientProfileScenariosToLinkedTable,
  mapEntityDispositionScenariosToLinkedTable,
  mapTestamentaryEntityScenariosToLinkedTable,
} from './DispositiveProvisionsTemplateSplitScreenModal.utils';
import { useLinkedEntities } from './hooks/useLinkedEntities';
import { DispositiveProvisionsTemplateABCTrustPreset } from './presets/DispositiveProvisionsTemplateSplitScreenModal.abcTrust';
import { DispositiveProvisionsTemplateABTrustPreset } from './presets/DispositiveProvisionsTemplateSplitScreenModal.abTrust';
import { DispositiveProvisionsTemplateMaritalTrustPreset } from './presets/DispositiveProvisionsTemplateSplitScreenModal.maritalTrust';
import { DispositiveProvisionsTemplateOutrightToSurvivingSpousePreset } from './presets/DispositiveProvisionsTemplateSplitScreenModal.outrightToSurvivingSpouse';
import { DispositiveProvisionsTemplatePouroverWillPreset } from './presets/DispositiveProvisionsTemplateSplitScreenModal.pourover';

export function DispositiveProvisionsTemplateHeader() {
  const canEditDP = useCanEditDispositiveProvisions();
  const [isEditing, setEditing] = useState(false);
  const { control, setValue } =
    useFormContext<DispositiveProvisionsTemplateShape>();

  const [displayName] = useWatch({
    control,
    name: [`${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.displayName`],
  });

  return isEditing && canEditDP ? (
    <TextInput
      value={displayName}
      autoFocus
      onChange={(e) => {
        setValue(
          `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.displayName`,
          e.target.value
        );
      }}
      onBlur={() => setEditing(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') setEditing(false);
      }}
      label="Template name"
      hideLabel
      placeholder="Template name"
      required
    />
  ) : (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="title-xl">
          {displayName || 'Untitled template'}
        </Typography>
        {canEditDP && (
          <Button
            variant="transparent"
            size="sm"
            onClick={() => setEditing(true)}
            startIcon={Edit02Icon}
          />
        )}
      </Stack>
    </Stack>
  );
}

interface DispositiveProvisionsTemplateHeaderContextMenuProps {
  onDelete: () => void;
}

export function DispositiveProvisionsTemplateHeaderContextMenu({
  onDelete,
}: DispositiveProvisionsTemplateHeaderContextMenuProps) {
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();

  return (
    <>
      <ContextMenuButton>
        <MenuItem
          variant="destructive"
          label="Delete template"
          onClick={openModal}
          icon={<Trash01Icon size={16} />}
        />
      </ContextMenuButton>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={(reason) => {
          if (reason === 'confirm') {
            onDelete();
          }
          closeModal();
        }}
        confirmText="Delete"
        confirmButtonProps={{
          variant: 'destructive-primary',
        }}
        cancelText="Cancel"
        heading="Are you sure you wish to delete this template?"
      >
        Entities currently using this template will be reset and will require
        new dispositions to be added
      </ConfirmationModal>
    </>
  );
}

export function TemplateUseCountBadge() {
  const { entities, testamentaryEntities, individuals, loading } =
    useLinkedEntities();
  const templateUseCount =
    entities.length + testamentaryEntities.length + individuals.length;

  if (loading) return <Skeleton variant="text" width={40} />;
  if (templateUseCount === 0) return null;

  return (
    <Badge
      display={`Linked to ${templateUseCount} ${templateUseCount === 1 ? 'entity' : 'entities'}`}
      variant={BadgeVariants.Warning}
    />
  );
}

export interface DispositiveProvisionsTemplateLinkedEntitiesProps {
  isFromEditingDispositions: boolean;
  setDispositiveProvisionsModalDetails?: (
    details: DispositiveProvisionsModalDetails | null
  ) => void;
}

export function DispositiveProvisionsTemplateLinkedEntities({
  isFromEditingDispositions,
  setDispositiveProvisionsModalDetails,
}: DispositiveProvisionsTemplateLinkedEntitiesProps) {
  const { control } = useFormContext<DispositiveProvisionsTemplateShape>();

  const [templateId] = useWatch({
    control,
    name: [`${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.id`],
  });

  const { entities, testamentaryEntities, individuals } = useLinkedEntities();

  const entityRows = mapEntityDispositionScenariosToLinkedTable(
    entities,
    templateId,
    setDispositiveProvisionsModalDetails
  );

  const testamentaryEntityRows = mapTestamentaryEntityScenariosToLinkedTable(
    testamentaryEntities,
    templateId,
    setDispositiveProvisionsModalDetails
  );

  const individualRows = mapClientProfileScenariosToLinkedTable(
    individuals,
    templateId,
    setDispositiveProvisionsModalDetails
  );

  const rows = [...entityRows, ...testamentaryEntityRows, ...individualRows];
  return (
    <>
      <Stack divider={<Divider sx={{ borderColor: COLORS.GRAY[200] }} />}>
        {rows.length === 0 && (
          <Typography variant="body-s">
            This template is not used on any entity
          </Typography>
        )}
        {rows.map(({ lineOne, lineTwo, id, onClick }) => (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            key={id}
            sx={{
              p: 1.5,
              cursor: isFromEditingDispositions ? 'cursor' : 'pointer',
            }}
            role={isFromEditingDispositions ? undefined : 'button'}
            tabIndex={0}
            onClick={isFromEditingDispositions ? noop : onClick}
          >
            <RichListItem heading={lineOne} description={lineTwo} />
            {isFromEditingDispositions ? null : (
              <ChevronRightIcon sx={{ color: COLORS.TEXT.SECONDARY }} />
            )}
          </Stack>
        ))}
      </Stack>
    </>
  );
}

const PRO_RATA_OPTIONS = [
  {
    label: 'Pro rata',
    value: ProRataValue.PRO_RATA,
    contextualHelp: (
      <PopperContent body="Disposition amounts will be sourced from the total sum of all entities linked to this template." />
    ),
  },
  {
    label: 'Per entity',
    value: ProRataValue.PER_ENTITY,
    contextualHelp: (
      <PopperContent body="Disposition amounts will be sourced from each entity linked to this template." />
    ),
  },
];

export function DispositiveProvisionsTemplateAdvancedSettings() {
  const { control } = useFormContext<DispositiveProvisionsTemplateShape>();

  return (
    <Card variant="outlined" sx={{ p: 2, flexShrink: 0 }}>
      <Stack spacing={1}>
        <Stack spacing={0.25}>
          <Typography variant="title-xs">Advanced settings</Typography>
          <Typography variant="body-xs">
            In the instance this template is applied to multiple entities, how
            should the distributions be sourced
          </Typography>
        </Stack>
        <FormAwareRadioGroup<DispositiveProvisionsTemplateShape>
          fieldName={
            `${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.proRata` as const satisfies PathsOf<DispositiveProvisionsTemplateShape>
          }
          control={control}
          label="Distribution sourcing"
          hideLabel
          options={PRO_RATA_OPTIONS}
          row
        />
      </Stack>
    </Card>
  );
}

/**
 * Context menu for template editor with residuary toggle, split disposition,
 * and remove all recipients.
 * This mirrors the PrimaryClientDeathSectionContextMenu but for templates.
 */
interface DispositiveProvisionsTemplateContextMenuProps {
  recipients?: FieldArrayWithId<
    DispositiveProvisionsFormShape,
    'dispositiveProvisions.recipients',
    '_id'
  >[];
  onBulkAddRecipients?: (
    recipients: DispositiveProvisionsFormRecipient[]
  ) => void;
}

export function DispositiveProvisionsTemplateContextMenu({
  recipients = [],
  onBulkAddRecipients,
}: DispositiveProvisionsTemplateContextMenuProps) {
  const canEditDP = useCanEditDispositiveProvisions();
  const { control, setValue } =
    useFormContext<DispositiveProvisionsTemplateShape>();

  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [{ isModalOpen: isRemoveAllModalOpen }, { setModalIsOpen }] =
    useModalState();

  const handleOpenSplitModal = useCallback(() => {
    setIsSplitModalOpen(true);
  }, []);

  const handleSplitResult = useCallback(
    (result: SplitDispositionModalResult | null) => {
      setIsSplitModalOpen(false);
      if (!result) return;
      onBulkAddRecipients?.(result.recipients);
    },
    [onBulkAddRecipients]
  );

  const handleRemoveAllRecipients = useCallback(() => {
    setValue(`${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`, [], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [setValue]);

  const hasRecipients = recipients.length > 0;

  if (!canEditDP) return null;

  return (
    <>
      {isSplitModalOpen && (
        <SplitDispositionModal
          isOpen={isSplitModalOpen}
          onClose={handleSplitResult}
          deathEvent={PrimaryClientDeathEvent.FIRST_DEATH}
        />
      )}
      {isRemoveAllModalOpen && (
        <ConfirmationModal
          isOpen={isRemoveAllModalOpen}
          heading="Remove all recipients"
          cancelText="Cancel"
          confirmText="Remove all"
          confirmButtonProps={{ variant: 'destructive-primary' }}
          onClose={(result) => {
            if (result === 'confirm') {
              handleRemoveAllRecipients();
            }
            setModalIsOpen(false);
          }}
        >
          All recipients will be removed.{' '}
          <Typography component="span" variant="body-xs" fontWeight="bold">
            This cannot be undone.
          </Typography>
        </ConfirmationModal>
      )}
      <ContextMenuButton
        size="xs"
        variant="transparent"
        data-testid="DispositiveProvisions-menu"
      >
        <MenuItem
          key="residuary-taxes-toggle"
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormAwareCheckbox<DispositiveProvisionsTemplateShape>
                fieldName={`${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}._residuaryPaysAllTaxes`}
                control={control}
                label=""
              />
              <Typography variant="body-xs">
                All taxes paid out of residuary
              </Typography>
              <ContextualHelpTooltip>
                <ResiduaryContextualHelp />
              </ContextualHelpTooltip>
            </Stack>
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
        <ToggleGrowAllByInflationMenuItem
          recipients={recipients}
          recipientsBasePath={DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}
        />
        {onBulkAddRecipients && (
          <MenuItem
            key="split-disposition"
            icon={<Reflect01Icon size={16} color={COLORS.GRAY['500']} />}
            label="Create evenly split disposition"
            onClick={handleOpenSplitModal}
          />
        )}
        {hasRecipients && (
          <>
            <SelectItemGroupLabel />
            <MenuItem
              key="remove-all-recipients"
              variant="destructive"
              icon={<Trash01Icon size={16} />}
              label="Remove all recipients"
              onClick={(event) => {
                event.stopPropagation();
                setModalIsOpen(true);
              }}
            />
          </>
        )}
      </ContextMenuButton>
    </>
  );
}

interface DispositiveProvisionsTemplateEditorTabHeaderProps {
  templateType: DispositiveProvisionTemplateKind;
  /**
   * Intended to be the output of `useFieldArray.fields`
   */
  recipients: (DispositiveProvisionsFormRecipient & { _id: string })[];
  onAddTemplateRecipient?: (
    value:
      | DispositiveProvisionsFormRecipient
      | DispositiveProvisionsFormRecipient[],
    options?: { shouldFocus?: boolean }
  ) => void;
}

export function DispositiveProvisionsTemplateEditorTabHeader({
  templateType,
  recipients,
  onAddTemplateRecipient,
}: DispositiveProvisionsTemplateEditorTabHeaderProps) {
  const onAddRecipient = useCallback(
    (input?: Partial<DispositiveProvisionsFormRecipient>) => {
      if (input) {
        onAddTemplateRecipient?.(
          { ...defaultRecipient, ...input },
          { shouldFocus: false }
        );
      } else {
        onAddTemplateRecipient?.(defaultRecipient, { shouldFocus: false });
      }
    },
    [onAddTemplateRecipient]
  );

  const onBulkAddRecipients = useCallback(
    (newRecipients: DispositiveProvisionsFormRecipient[]) => {
      // react-hook-form's append accepts an array for bulk-adding
      onAddTemplateRecipient?.(newRecipients);
    },
    [onAddTemplateRecipient]
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <PrimaryClientDeathSectionDistributionStatus
          templateKind={templateType}
          recipients={recipients}
          onAddRecipient={onAddRecipient}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrimaryClientDeathSectionActions
            deathEvent={PrimaryClientDeathEvent.FIRST_DEATH}
            templateKind={templateType}
            onAddRecipient={onAddRecipient}
          />
          <DispositiveProvisionsTemplateContextMenu
            recipients={recipients}
            onBulkAddRecipients={onBulkAddRecipients}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

export function DispositiveProvisionsTemplateEditorTab() {
  const { control } = useFormContext<DispositiveProvisionsTemplateShape>();
  const [templateType] = useWatch({
    control,
    name: [`${DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE}.templateType`],
  });

  let editorContent: ReactNode = null;

  switch (templateType) {
    case DispositiveProvisionTemplateKind.PouroverWill:
      editorContent = <DispositiveProvisionsTemplatePouroverWillPreset />;
      break;
    case DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse:
      editorContent = (
        <DispositiveProvisionsTemplateOutrightToSurvivingSpousePreset />
      );
      break;
    case DispositiveProvisionTemplateKind.MaritalTrust:
      editorContent = <DispositiveProvisionsTemplateMaritalTrustPreset />;
      break;
    case DispositiveProvisionTemplateKind.AbTrust:
      editorContent = <DispositiveProvisionsTemplateABTrustPreset />;
      break;
    case DispositiveProvisionTemplateKind.AbcTrust:
      editorContent = <DispositiveProvisionsTemplateABCTrustPreset />;
      break;
    case DispositiveProvisionTemplateKind.Default:
    default:
      // DispositiveProvisionsRecipients renders DispositiveProvisionsTemplateEditorTabHeader
      // (which includes the context menu) when templateType is set
      editorContent = (
        <DispositiveProvisionsRecipients
          // because templates are death-order agnostic, there's no death order
          // for a disposition scheme to apply to; instead, the template will
          // be attached to an existing disposition scheme
          onOpenSchemeSelectionModal={null}
          defaultExpandAll
          templateType={DispositiveProvisionTemplateKind.Default}
        />
      );
  }

  return editorContent;
}
